import { useEffect, useState, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import DOMPurify from "dompurify";
import {
  ArrowLeft, Calendar, Clock, Video, FileText, ScrollText, Award,
  ListChecks, History as HistoryIcon, ExternalLink, Download, CheckCircle2,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { PhaseChecklist } from "@/components/PhaseChecklist";
import { Skeleton } from "@/components/ui/skeleton";
import { api, formatDateBR, formatDateTimeBR, monthName } from "@/lib/api";

export default function PortalContract() {
  const { id } = useParams();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get(`/portal/contracts/${id}`).then(({ data }) => setData(data));
  }, [id]);

  // Sanitize welcome HTML (TipTap output) — MUST be called unconditionally
  const sanitizedWelcome = useMemo(() => {
    const html = data?.contract?.welcome_message;
    if (!html) return "";
    return DOMPurify.sanitize(html, {
      ALLOWED_TAGS: ["p", "br", "strong", "b", "em", "i", "u", "ul", "ol", "li", "a", "h1", "h2", "h3", "blockquote", "code"],
      ALLOWED_ATTR: ["href", "target", "rel", "class"],
      ADD_ATTR: ["target", "rel"],
    });
  }, [data?.contract?.welcome_message]);

  if (!data) return <Skeleton className="h-96" />;

  const { contract, meetings, documents, reports, timeline } = data;
  const upcoming = meetings.filter(m => new Date(m.scheduled_at) >= new Date());
  const past = meetings.filter(m => new Date(m.scheduled_at) < new Date());

  // Heurística: se o conteúdo parece HTML (contém tags), renderiza como HTML
  const isHtml = contract.welcome_message?.includes("<") && contract.welcome_message?.includes(">");

  return (
    <div className="space-y-8">
      <Link to="/portal" className="inline-flex items-center text-sm text-slate-600 hover:text-slate-900">
        <ArrowLeft className="w-4 h-4 mr-1" /> Voltar ao Portal
      </Link>

      <section className="portal-hero-bg rounded-2xl border border-slate-200 p-8 md:p-10">
        <div className="text-xs uppercase tracking-[0.2em] text-gold-deep mb-2">Contrato</div>
        <h1 className="serif text-4xl font-semibold text-slate-900">{contract.product_name}</h1>
        <div className="mt-4 flex flex-wrap gap-x-6 gap-y-2 text-sm text-slate-700">
          <div>Início: <strong>{formatDateBR(contract.start_date)}</strong></div>
          <div>Término: <strong>{formatDateBR(contract.end_date)}</strong></div>
        </div>
        <div className="mt-6">
          <div className="flex items-center gap-3">
            <Progress value={contract.progress_pct} className="h-2 flex-1" />
            <span className="text-sm font-semibold text-slate-900">{contract.progress_pct}%</span>
          </div>
          {contract.product_phases?.length > 0 && (
            <div className="text-xs text-slate-600 mt-2">
              Fase atual: <strong>{contract.product_phases[contract.current_phase_index]?.title}</strong>
              {' '}({contract.current_phase_index + 1} de {contract.product_phases.length})
            </div>
          )}
        </div>
      </section>

      {/* "Comece por aqui" — welcome message */}
      {contract.welcome_message && (
        <section className="bg-gold/10 border-l-4 border-gold rounded-r-lg p-6" data-testid="portal-welcome-section">
          <div className="text-xs uppercase tracking-[0.18em] text-gold-deep font-semibold mb-2">
            Comece por aqui
          </div>
          {isHtml ? (
            <div
              className="prose prose-slate prose-sm max-w-none serif text-base text-slate-800 leading-relaxed [&_a]:text-midnight [&_a]:underline [&_a:hover]:text-ink"
              dangerouslySetInnerHTML={{ __html: sanitizedWelcome }}
              data-testid="portal-welcome-content"
            />
          ) : (
            <p className="serif text-base text-slate-800 leading-relaxed whitespace-pre-line" data-testid="portal-welcome-content">
              {contract.welcome_message}
            </p>
          )}
        </section>
      )}

      <Tabs defaultValue="phases" className="space-y-6">
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="phases"><ListChecks className="w-4 h-4 mr-2" /> Fases</TabsTrigger>
          <TabsTrigger value="meetings"><Calendar className="w-4 h-4 mr-2" /> Reuniões</TabsTrigger>
          <TabsTrigger value="minutes"><ScrollText className="w-4 h-4 mr-2" /> Atas e gravações</TabsTrigger>
          <TabsTrigger value="docs"><FileText className="w-4 h-4 mr-2" /> Documentos</TabsTrigger>
          <TabsTrigger value="reports"><Award className="w-4 h-4 mr-2" /> Prestações</TabsTrigger>
          <TabsTrigger value="timeline"><HistoryIcon className="w-4 h-4 mr-2" /> Histórico</TabsTrigger>
        </TabsList>

        <TabsContent value="phases">
          <Card className="card-soft">
            <CardHeader>
              <CardTitle className="serif text-xl">Fluxo do contrato</CardTitle>
            </CardHeader>
            <CardContent>
              <PhaseChecklist
                phases={contract.product_phases || []}
                phasesState={contract.phases_state || []}
                variant="portal"
              />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="meetings" className="space-y-6">
          <Card className="card-soft">
            <CardHeader>
              <CardTitle className="serif text-xl">Próximas reuniões</CardTitle>
            </CardHeader>
            <CardContent>
              {upcoming.length === 0 ? (
                <div className="text-sm text-slate-500 py-6 text-center">Sem reuniões agendadas.</div>
              ) : (
                <div className="space-y-3">
                  {upcoming.map(m => (
                    <div key={m.id} className="p-4 border border-slate-200 rounded-lg">
                      <div className="flex items-start justify-between gap-3 flex-wrap">
                        <div>
                          <div className="font-medium text-slate-900">{m.title}</div>
                          <div className="text-sm text-slate-600 mt-0.5 flex items-center gap-1">
                            <Clock className="w-3.5 h-3.5" /> {formatDateTimeBR(m.scheduled_at)} · {m.duration_minutes} min
                          </div>
                          {m.agenda && <div className="text-sm text-slate-600 mt-1">{m.agenda}</div>}
                        </div>
                        {m.room_url && !m.room_placeholder && (
                          <a href={m.room_url} target="_blank" rel="noreferrer"
                            className="inline-flex items-center gap-2 text-xs font-medium px-3 py-1.5 rounded-md bg-midnight text-white">
                            <Video className="w-3.5 h-3.5" /> Entrar
                          </a>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="card-soft">
            <CardHeader>
              <CardTitle className="serif text-xl">Reuniões realizadas</CardTitle>
            </CardHeader>
            <CardContent>
              {past.length === 0 ? (
                <div className="text-sm text-slate-500 py-6 text-center">Sem reuniões realizadas.</div>
              ) : (
                <div className="space-y-3">
                  {past.map(m => (
                    <div key={m.id} className="p-4 border border-slate-200 rounded-lg">
                      <div className="flex items-start justify-between gap-3 flex-wrap">
                        <div className="flex-1">
                          <div className="font-medium text-slate-900">{m.title}</div>
                          <div className="text-sm text-slate-600 mt-0.5 flex items-center gap-1">
                            <Clock className="w-3.5 h-3.5" /> {formatDateTimeBR(m.scheduled_at)}
                          </div>
                          {m.minutes_text && (
                            <p className="mt-2 text-sm text-slate-700 line-clamp-2">{m.minutes_text}</p>
                          )}
                        </div>
                        {m.minutes_published && (
                          <span className="text-xs px-2 py-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
                            <CheckCircle2 className="inline w-3 h-3 mr-1" /> Ata disponível
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="minutes">
          <Card className="card-soft">
            <CardHeader>
              <CardTitle className="serif text-xl">Atas e gravações publicadas</CardTitle>
            </CardHeader>
            <CardContent>
              {past.filter(m => m.minutes_published || m.vimeo_video_id || m.drive_recording_embed_url).length === 0 ? (
                <div className="text-sm text-slate-500 py-6 text-center">Nenhuma ata ou gravação publicada ainda.</div>
              ) : (
                <div className="space-y-6">
                  {past.filter(m => m.minutes_published || m.vimeo_video_id || m.drive_recording_embed_url).map(m => (
                    <article key={m.id} className="border border-slate-200 rounded-lg p-5" data-testid={`portal-meeting-${m.id}`}>
                      <header className="flex items-start justify-between gap-3 flex-wrap mb-3">
                        <div>
                          <h3 className="serif text-lg text-slate-900">{m.title}</h3>
                          <div className="text-xs text-slate-500">{formatDateTimeBR(m.scheduled_at)}</div>
                        </div>
                        <div className="flex items-center gap-2 flex-wrap">
                          {(m.drive_recording_embed_url || m.vimeo_video_id) && (
                            <span className="text-xs px-2 py-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 inline-flex items-center gap-1">
                              <Video className="w-3 h-3" /> Gravação disponível
                            </span>
                          )}
                          {m.minutes_published && (
                            <span className="text-xs px-2 py-1 rounded-full bg-blue-50 text-blue-700 border border-blue-200 inline-flex items-center gap-1">
                              <ScrollText className="w-3 h-3" /> Ata publicada
                            </span>
                          )}
                        </div>
                      </header>

                      {/* Gravação: Melhoria 1 (2026-06-17) — Vimeo (manual) tem prioridade sobre Drive (auto) */}
                      {m.vimeo_video_id ? (
                        <div
                          className="relative w-full bg-slate-900 rounded-lg overflow-hidden mb-4"
                          style={{ paddingBottom: "56.25%" }}
                          data-testid={`portal-vimeo-player-${m.id}`}
                        >
                          <iframe
                            src={`https://player.vimeo.com/video/${m.vimeo_video_id}${m.vimeo_hash ? `?h=${m.vimeo_hash}` : ""}`}
                            className="absolute inset-0 w-full h-full"
                            frameBorder="0"
                            allow="autoplay; fullscreen; picture-in-picture"
                            allowFullScreen
                            title={`Gravação: ${m.title}`}
                          />
                        </div>
                      ) : m.drive_recording_embed_url ? (
                        <div
                          className="relative w-full bg-slate-900 rounded-lg overflow-hidden mb-4"
                          style={{ paddingBottom: "56.25%" }}
                          data-testid={`portal-drive-player-${m.id}`}
                        >
                          <iframe
                            src={m.drive_recording_embed_url}
                            className="absolute inset-0 w-full h-full"
                            frameBorder="0"
                            allow="autoplay; fullscreen"
                            allowFullScreen
                            title={`Gravação: ${m.title}`}
                          />
                        </div>
                      ) : null}

                      {/* Recording legacy URL (fallback antigo) */}
                      {!m.drive_recording_embed_url && !m.vimeo_video_id && m.recording_url && (
                        <a href={m.recording_url} target="_blank" rel="noreferrer"
                          className="inline-flex items-center gap-2 text-xs font-medium px-3 py-1.5 rounded-md border border-slate-300 text-slate-700 hover:bg-slate-50 mb-3">
                          <Video className="w-3.5 h-3.5" /> Assistir gravação <ExternalLink className="w-3 h-3" />
                        </a>
                      )}

                      {/* Ata */}
                      {m.minutes_text && (
                        <p className="text-sm text-slate-700 whitespace-pre-line">{m.minutes_text}</p>
                      )}
                    </article>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="docs">
          <Card className="card-soft">
            <CardHeader>
              <CardTitle className="serif text-xl">Documentos aprovados</CardTitle>
            </CardHeader>
            <CardContent>
              {documents.length === 0 ? (
                <div className="text-sm text-slate-500 py-6 text-center">Nenhum documento publicado ainda.</div>
              ) : (
                <div className="space-y-3">
                  {documents.map(d => (
                    <div key={d.id} className="p-4 border border-slate-200 rounded-lg flex items-start justify-between gap-3 flex-wrap">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <FileText className="w-4 h-4 text-slate-500" />
                          <span className="font-medium text-slate-900">{d.title}</span>
                          <span className="text-xs text-slate-500">v{d.current_version}</span>
                        </div>
                        {d.description && <p className="text-sm text-slate-600 mt-1">{d.description}</p>}
                        <div className="text-xs text-slate-500 mt-1">Publicado em {formatDateBR(d.published_at)}</div>
                      </div>
                      <a href={d.file_url} download={d.file_name}
                        className="inline-flex items-center gap-2 text-xs font-medium px-3 py-1.5 rounded-md bg-midnight text-white">
                        <Download className="w-3.5 h-3.5" /> Baixar
                      </a>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="reports">
          <Card className="card-soft">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="serif text-xl">Prestações de contas</CardTitle>
              <Link to={`/portal/contratos/${id}/prestacao`}>
                <Button size="sm" className="bg-gold text-ink hover:bg-gold-deep hover:text-white" data-testid="portal-open-prestacao">
                  <Award className="w-4 h-4 mr-1" /> Gerar prestação de contas
                </Button>
              </Link>
            </CardHeader>
            <CardContent>
              {reports.length === 0 ? (
                <div className="text-sm text-slate-500 py-6 text-center">
                  Nenhuma prestação gerada ainda. Use o botão acima para gerar a primeira.
                </div>
              ) : (
                <div className="space-y-3">
                  {reports.map(r => (
                    <Link
                      key={r.id}
                      to={`/portal/contratos/${id}/prestacao`}
                      className="block border border-slate-200 rounded-lg p-4 hover:border-midnight hover:bg-slate-50 transition-colors"
                      data-testid={`portal-prestacao-${r.id}`}
                    >
                      <div className="flex items-center justify-between gap-3">
                        <div>
                          <h3 className="serif text-lg text-slate-900">
                            Prestação de Contas — {monthName(r.month)} / {r.year}
                          </h3>
                          <div className="text-xs text-slate-500 mt-0.5">
                            Gerada em {formatDateTimeBR(r.generated_at)}
                            {r.generated_by_name && <> por <strong>{r.generated_by_name}</strong></>}
                          </div>
                        </div>
                        <span className="text-xs px-2 py-1 rounded-full bg-blue-50 text-blue-700 border border-blue-200">
                          PDF disponível
                        </span>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="timeline">
          <Card className="card-soft">
            <CardHeader>
              <CardTitle className="serif text-xl">Histórico do contrato</CardTitle>
            </CardHeader>
            <CardContent>
              {timeline.length === 0 ? (
                <div className="text-sm text-slate-500 py-6 text-center">Sem eventos.</div>
              ) : (
                <ol className="relative ml-3 border-l border-slate-200 space-y-6">
                  {timeline.map(e => (
                    <li key={e.id} className="ml-4">
                      <span className="absolute -left-[7px] mt-1 w-3 h-3 rounded-full bg-midnight"></span>
                      <div className="text-sm text-slate-900">{e.details}</div>
                      <div className="text-xs text-slate-500">{formatDateTimeBR(e.created_at)}</div>
                    </li>
                  ))}
                </ol>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
